import {
  ExtranetContainer,
  ExtranetEditButton,
  formatDateString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacColumn,
  PharmacContainer,
  PharmacFieldset,
  PharmacFieldsetLegend,
  PharmacH5,
  PharmacH6,
  PharmacIcon,
  PharmacLink,
  PharmacRow,
  PharmacText,
} from '@pharmac/ui'
import { __PRIVACY_POLICY_LINK, __TERMS_OF_USE_LINK } from '../../constants'
import { AuthenticatedUserDto } from '../../features/authentication/dto'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { ApprovalSwitch } from './ApprovalSwitch'
import { UserCustomerRoles } from './UserCustomerRoles'

type ApprovalChangeFn = (val: boolean) => void

type Props = {
  editButtonClick: () => void
  userInfo: AuthenticatedUserDto
  marketingOnChange: ApprovalChangeFn
  cookieOnChange: ApprovalChangeFn
  tosOnChange: ApprovalChangeFn
  privacyOnChange: ApprovalChangeFn
}

export const UserInfoForm = ({
  editButtonClick,
  userInfo,
  /* cookieOnChange, */
  marketingOnChange,
  privacyOnChange,
  tosOnChange,
}: Props) => {
  const { user } = useAuthenticationContext()
  const { t } = useLanguage(['extranet', 'common'])
  const { minDesktop, maxMobile, minTablet } = useBreakpoint()

  return (
    <>
      <PharmacFieldset style={maxMobile ? { padding: 0 } : {}}>
        <PharmacFieldsetLegend marginSmallLeft={maxMobile}>
          <PharmacH5>{t('basicInfo', { ns: 'common' })}</PharmacH5>
        </PharmacFieldsetLegend>
        <ExtranetContainer
          justifyEnd
          paddingMediumRight={!maxMobile}
          paddingLargeRight={maxMobile}
          paddingMediumTop={maxMobile}
        >
          <PharmacRow>
            <ExtranetEditButton onClick={editButtonClick} />
          </PharmacRow>
        </ExtranetContainer>
        <ExtranetContainer
          full
          flexColumn={!minDesktop}
          paddingMediumLeft={minDesktop}
        >
          <ExtranetContainer
            flexColumn
            half={minDesktop}
            full={!minDesktop}
          >
            <PharmacColumn paddingMediumLeft>
              <PharmacH6>{t('lastName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{user?.lastName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('firstName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{user?.firstName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('emailAddress', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{user?.emailAddress || '-'}</PharmacText>
              </PharmacContainer>
              {minDesktop && (
                <>
                  <PharmacH6 paddingMediumTop>{t('userName', { ns: 'common' })}</PharmacH6>
                  <PharmacContainer
                    marginSmallTop
                    marginMediumBottom
                  >
                    <PharmacText>{user?.username || '-'}</PharmacText>
                  </PharmacContainer>
                  <PharmacH6 paddingMediumTop>
                    {t('certificateCardNumber', { ns: 'common' })}
                  </PharmacH6>
                  <PharmacContainer
                    marginSmallTop
                    marginMediumBottom
                  >
                    <PharmacText>{user?.certificateCardNumber || '-'}</PharmacText>
                  </PharmacContainer>
                </>
              )}
            </PharmacColumn>
          </ExtranetContainer>

          <ExtranetContainer
            flexColumn
            half={minDesktop}
            full={!minDesktop}
          >
            <PharmacColumn
              paddingMediumLeft
              paddingMediumTop={!minDesktop}
            >
              <PharmacH6>{t('phoneNumber', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{user?.phoneNumber || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('streetName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{user?.streetName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('postCode', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{user?.postCode || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('townName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{user?.townName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('countryCode', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{user?.countryCode || '-'}</PharmacText>
              </PharmacContainer>
              {!minDesktop && (
                <>
                  <PharmacH6 paddingMediumTop>{t('userName', { ns: 'common' })}</PharmacH6>
                  <PharmacContainer
                    marginSmallTop
                    marginMediumBottom
                  >
                    <PharmacText>{user?.username || '-'}</PharmacText>
                  </PharmacContainer>
                  <PharmacH6 paddingMediumTop>
                    {t('certificateCardNumber', { ns: 'common' })}
                  </PharmacH6>
                  <PharmacContainer
                    marginSmallTop
                    marginMediumBottom
                  >
                    <PharmacText>{user?.certificateCardNumber || '-'}</PharmacText>
                  </PharmacContainer>
                </>
              )}
            </PharmacColumn>
          </ExtranetContainer>
        </ExtranetContainer>
      </PharmacFieldset>

      <UserCustomerRoles />

      <ExtranetContainer
        paddingLargeTop
        paddingLargeLeft={minDesktop}
        flexColumn
      >
        <ExtranetContainer
          twoThirds={minTablet}
          full={!minDesktop}
          paddingLargeLeft={minDesktop}
          flexColumn
        >
          <ApprovalSwitch
            name=""
            label={t('user.marketingAuthorization', { ns: 'extranet' })}
            initialValue={userInfo.marketingAuthorizationDate}
            onChange={marketingOnChange}
            marginMediumBottom
          >
            <PharmacText
              paddingSmallTop
              paddingMediumBottom
            >
              {t('user.marketingAuthorizationInfo', { ns: 'extranet' })}
            </PharmacText>
            {user?.marketingAuthorizationDate && (
              <PharmacText>
                {t('user.marketingAuthorizationApproved', { ns: 'extranet' })}:{' '}
                {formatDateString(user?.marketingAuthorizationDate)}
              </PharmacText>
            )}
          </ApprovalSwitch>
          <ApprovalSwitch
            name=""
            label={t('user.privacyPolicy', { ns: 'extranet' })}
            initialValue={userInfo.privacyPolicyApprovalDate}
            onChange={privacyOnChange}
            disabled={userInfo.privacyPolicyApprovalDate !== null}
            marginMediumBottom
            marginMediumTop
          >
            <PharmacText
              paddingSmallTop
              paddingMediumBottom
            >
              {t('user.privacyPolicyInfo', { ns: 'extranet' })}
            </PharmacText>
            <PharmacLink
              href={__PRIVACY_POLICY_LINK}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PharmacH6
                style={{ textDecoration: 'underline', display: 'flex', flexDirection: 'row' }}
                paddingSmallRight
                color="purple"
              >
                {t('user.privacyPolicyLinkText', { ns: 'extranet' })}
                <div style={{ marginRight: 'var(--spacing-small)' }}></div>
                <PharmacIcon
                  stroke="purple"
                  size="16px"
                  icon="externalLink"
                />
              </PharmacH6>
            </PharmacLink>
            {user?.privacyPolicyApprovalDate && (
              <PharmacText paddingMediumTop>
                {t('user.privacyPolicyApproved', { ns: 'extranet' })}:{' '}
                {formatDateString(user?.privacyPolicyApprovalDate)}
              </PharmacText>
            )}
          </ApprovalSwitch>

          <ApprovalSwitch
            name=""
            label={t('user.termsOfUse', { ns: 'extranet' })}
            initialValue={userInfo.extranetTermsOfUseApprovalDate}
            onChange={tosOnChange}
            disabled={userInfo.extranetTermsOfUseApprovalDate !== null}
            marginMediumBottom
            marginMediumTop
          >
            <PharmacText
              paddingSmallTop
              paddingMediumBottom
            >
              {t('user.termsOfUseInfo', { ns: 'extranet' })}
            </PharmacText>
            <PharmacLink
              href={__TERMS_OF_USE_LINK}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PharmacH6
                style={{ textDecoration: 'underline', display: 'flex', flexDirection: 'row' }}
                paddingSmallRight
                color="purple"
              >
                {t('user.termsOfUseLinkText', { ns: 'extranet' })}
                <div style={{ marginRight: 'var(--spacing-small)' }}></div>
                <PharmacIcon
                  stroke="purple"
                  size="16px"
                  icon="externalLink"
                />
              </PharmacH6>
            </PharmacLink>
            {user?.extranetTermsOfUseApprovalDate && (
              <PharmacText paddingMediumTop>
                {t('user.termsOfUseApproved', { ns: 'extranet' })}:{' '}
                {formatDateString(user?.extranetTermsOfUseApprovalDate)}
              </PharmacText>
            )}
          </ApprovalSwitch>
        </ExtranetContainer>
      </ExtranetContainer>
    </>
  )
}
